(ns minorodata.components.webforms.grid
  (:require
   ["ag-grid-react" :refer [AgGridReact]]
   ["lucide-react" :refer [EllipsisVertical]]
   [applied-science.js-interop :as j]
   [ui.data-grid.core]
   [ui.data-grid.theme :as ag-theme]
   [react :as react]
   [reagent.core :as reagent]
   [ui.button :as button]
   [ui.dropdown-menu :refer [dropdown-menu-content dropdown-menu-item
                             dropdown-menu-root dropdown-menu-trigger]]
   [ui.tooltip :as tooltip]))

(def row-id-key :__webform-row-id)
(def ^:private padding-row-key :__webform-padding-row)

(def ^:private webforms-theme-params
  (merge
   ag-theme/kleene-token-params
   {:accentColor "var(--color-kleene-secondary-02)"
    :wrapperBorder false
    :wrapperBorderRadius 0
    :columnBorder {:style "solid" :width 1
                   :color "var(--color-kleene-layout-border)"}
    :headerColumnBorder {:style "solid" :width 1
                         :color "var(--color-kleene-layout-border)"}
    :rowBorder {:style "solid" :width 1
                :color "var(--color-kleene-layout-border)"}
    :selectedRowBackgroundColor "var(--color-kleene-selected-background)"
    :headerCellHoverBackgroundColor "var(--color-kleene-hover-background)"
    :cellEditingBorder {:style "solid" :width 1
                        :color "var(--color-kleene-secondary-02)"}
    :checkboxBorderRadius "2px"
    :checkboxBorderWidth "1px"
    :checkboxCheckedBackgroundColor "var(--color-kleene-secondary-02)"
    :checkboxCheckedBorderColor "var(--color-kleene-secondary-02)"
    :checkboxCheckedShapeColor "white"
    :checkboxIndeterminateBackgroundColor "var(--color-kleene-secondary-02)"
    :checkboxIndeterminateBorderColor "var(--color-kleene-secondary-02)"
    :checkboxIndeterminateShapeColor "white"
    :checkboxUncheckedBackgroundColor "var(--color-kleene-background)"
    :checkboxUncheckedBorderColor "var(--color-kleene-input-border)"
    :iconSize "20px"}))

(def ^:private light-theme
  (j/call ag-theme/light :withParams
          (clj->js
           (assoc webforms-theme-params :focusShadow
                  {:radius 0 :spread 2
                   :color "var(--color-kleene-secondary-05)"}))))

(def ^:private dark-theme
  (j/call ag-theme/dark :withParams
          (clj->js
           (assoc webforms-theme-params :focusShadow
                  {:radius 0 :spread 2
                   :color "color-mix(in srgb, var(--color-kleene-secondary-05) 30%, transparent)"}))))

(defn- theme-for-mode [dark-mode?]
  (if dark-mode? dark-theme light-theme))

(defn blank-value? [value]
  (or (nil? value) (= "" value)))

(defn meaningful-row? [row fields]
  (some #(not (blank-value? (get row %))) fields))

(defn baseline-by-id [rows]
  (into {} (keep (fn [row]
                   (when-let [uuid (:-kwf-uuid row)]
                     [uuid row])))
                 rows))

(defn prepare-rows
  ([rows fields items-per-page]
   (prepare-rows rows fields items-per-page #(str (random-uuid))))
  ([rows fields items-per-page make-id]
   (let [persisted (mapv #(assoc % row-id-key (:-kwf-uuid %)) rows)
         padding   (max 0 (- items-per-page (count rows)))]
     (into persisted
           (repeatedly padding
                       #(assoc (zipmap fields (repeat nil))
                               row-id-key (make-id)
                               padding-row-key true))))))

(defn new-row
  ([fields]
   (new-row fields #(str (random-uuid))))
  ([fields make-id]
   (assoc (zipmap fields (repeat nil)) row-id-key (make-id))))

(defn- safe-integer-string? [s]
  (let [n (js/BigInt s)]
    (and (<= n (js/BigInt js/Number.MAX_SAFE_INTEGER))
         (>= n (js/BigInt js/Number.MIN_SAFE_INTEGER)))))

(defn parse-value [{:keys [type]} value]
  (if (and (= "numeric" type)
           (string? value)
           (re-matches #"[+-]?\d+" value)
           (safe-integer-string? value))
    (js/Number value)
    value))

(defn validation-error [{:keys [type is-nullable]} value]
  (cond
    (blank-value? value) (when-not is-nullable :required)
    (and (= "numeric" type)
         (not (and (number? value) (int? value)))) :integer
    :else nil))

(defn row-status [row baseline fields deleted-ids]
  (let [uuid   (:-kwf-uuid row)
        row-id (get row row-id-key)]
    (cond
      (and uuid (contains? deleted-ids row-id)) :deleted
      (nil? uuid) (if (meaningful-row? row fields) :added :unchanged)
      (not= (select-keys row fields)
            (select-keys (get baseline uuid) fields)) :updated
      :else :unchanged)))

(defn ordered-values [row fields]
  (mapv #(get row %) fields))

(defn build-diffs
  ([rows baseline fields deleted-ids]
   (build-diffs rows baseline fields deleted-ids
                (fn [_] (str (random-uuid)))))
  ([rows baseline fields deleted-ids new-row-uuid]
   (into []
         (keep (fn [row]
                 (let [status (row-status row baseline fields deleted-ids)]
                   (when (not= :unchanged status)
                     {:button (case status
                                :added :add
                                :updated :update
                                :deleted :remove)
                      :values (ordered-values row fields)
                      :uuid   (or (:-kwf-uuid row) (new-row-uuid row))}))))
         rows)))

(defn current-rows [api]
  (let [rows (atom [])]
    (j/call api :forEachNode
            #(swap! rows conj
                    (js->clj (j/get % :data) :keywordize-keys true)))
    @rows))

(defn add-row! [api fields]
  (j/call api :applyTransaction
          #js {:add #js [(clj->js (new-row fields))]}))

(defn- row-id [row]
  (get row row-id-key))

(defn- invalid-cell? [column row fields deleted-ids touched-cells]
  (let [id        (row-id row)
        field     (:field column)
        persisted? (some? (:-kwf-uuid row))
        validate?  (or persisted?
                       (meaningful-row? row fields)
                       (contains? touched-cells [id field]))]
    (and (not (contains? deleted-ids id))
         validate?
         (some? (validation-error column (get row field))))))

(defn- refresh-rows! [api]
  (j/call api :redrawRows)
  (j/call api :refreshCells #js {:force true}))

(defn- delete-row! [api row-js deleted-ids-ref]
  (let [row (js->clj row-js :keywordize-keys true)]
    (if (:-kwf-uuid row)
      (set! (.-current deleted-ids-ref)
            (conj (.-current deleted-ids-ref) (row-id row)))
      (j/call api :applyTransaction #js {:remove #js [row-js]}))))

(defn- restore-row! [row deleted-ids-ref]
  (when (:-kwf-uuid row)
    (set! (.-current deleted-ids-ref)
          (disj (.-current deleted-ids-ref) (row-id row)))))

(defn- finish-row-operation! [api on-state-change]
  (on-state-change)
  (refresh-rows! api))

(defn delete-selected! [api deleted-ids-ref on-state-change]
  (doseq [row-js (array-seq (j/call api :getSelectedRows))]
    (delete-row! api row-js deleted-ids-ref))
  (j/call api :deselectAll)
  (finish-row-operation! api on-state-change))

(defn restore-selected! [api deleted-ids-ref on-state-change]
  (doseq [row-js (array-seq (j/call api :getSelectedRows))]
    (restore-row! (js->clj row-js :keywordize-keys true) deleted-ids-ref))
  (j/call api :deselectAll)
  (finish-row-operation! api on-state-change))

(defn- column-header [{:keys [title type is-nullable]}]
  (let [type-label (if (= "numeric" type) "Integer" "Text or Date")]
    [:div {:class "flex w-full items-center justify-between gap-1"}
     [:span {:class "min-w-0 overflow-hidden text-ellipsis"} title]
     [tooltip/tooltip
      {:content (str type-label
                     (if is-nullable "; nullable" "; not nullable"))}
      [:span {:class "flex shrink-0 gap-1 cursor-help"}
       [:span {:class "inline-flex h-5 items-center justify-center rounded bg-kleene-selected-background px-2 font-mono text-xs font-semibold leading-none text-kleene-text-secondary tabular-nums"}
        (if (= "numeric" type) "123" "abc")]
       (when-not is-nullable
         [:span {:class "inline-flex h-5 items-center justify-center rounded bg-kleene-selected-background px-2 font-mono text-xs font-semibold leading-none text-kleene-text-secondary"}
          "*"])]]]))

(defn- header-component [params]
  (reagent/as-element
   [column-header (js->clj (j/get-in params [:column :colDef :headerComponentParams])
                           :keywordize-keys true)]))

(defn- value-setter [column touched-cells-ref params]
  (let [row       (j/get params :data)
        row-id    (j/get row (name row-id-key))
        field     (:field column)
        old-value (j/get params :oldValue)
        new-value (parse-value column (j/get params :newValue))]
    (set! (.-current touched-cells-ref)
          (conj (.-current touched-cells-ref) [row-id field]))
    (when (not= old-value new-value)
      (j/assoc! row (name field) new-value)
      true)))

(defn- stop-propagation [event]
  (.stopPropagation event))

(defn- row-actions [{:keys [label on-action]}]
  [dropdown-menu-root
   [dropdown-menu-trigger {:as-child true}
    [:> button/button-fr
     {:variant :empty-transparent
      :size :icon-xs
      :title "Row actions"
      :aria-label "Row actions"
      :on-click stop-propagation
      :on-pointer-down stop-propagation}
     [:> EllipsisVertical {:size 16}]]]
   [dropdown-menu-content {:align "end" :hide-arrow true}
    [dropdown-menu-item
     {:on-click (fn [event]
                  (stop-propagation event)
                  (on-action))}
     label]]])

(defn- action-column [api-ref fields deleted-ids-ref on-state-change]
  (clj->js
   {"colId" "webform-row-actions" "headerName" ""
    "width" 36 "minWidth" 36 "maxWidth" 36 "pinned" "right"
    "lockPinned" true "lockPosition" true "suppressMovable" true
    "sortable" false "filter" false "resizable" false "editable" false
    "suppressHeaderMenuButton" true
    "cellStyle" #js {:display "flex" :alignItems "center"
                      :justifyContent "center" :padding 0}
    "cellRenderer"
    (fn [params]
      (let [row        (js->clj (j/get params :data) :keywordize-keys true)
            persisted? (some? (:-kwf-uuid row))
            deleted?   (contains? (.-current deleted-ids-ref) (row-id row))
            explicit-new? (and (not persisted?)
                               (not (get row padding-row-key)))]
        (when (or persisted? explicit-new? (meaningful-row? row fields))
          (reagent/as-element
           [row-actions
            {:label (cond
                      deleted? "Restore row"
                      persisted? "Delete row"
                      :else "Remove row")
             :on-action
             (fn []
               (when-let [api (.-current api-ref)]
                 (if deleted?
                   (restore-row! row deleted-ids-ref)
                   (delete-row! api (j/get params :data) deleted-ids-ref))
                 (finish-row-operation! api on-state-change)))}]))))}))

(defn- row-number-column [page items-per-page]
  (clj->js
   {"colId" "webform-row-number" "headerName" ""
    "valueGetter" #(+ (inc (* items-per-page (dec page)))
                      (j/get-in % [:node :rowIndex]))
    "width" 46 "minWidth" 46 "maxWidth" 46 "pinned" "left"
    "lockPinned" true "lockPosition" true "suppressMovable" true
    "sortable" false "filter" false "resizable" false "editable" false
    "cellStyle" #js {:textAlign "center"
                      :backgroundColor "var(--color-kleene-background)"}}))

(defn- data-column [column fields deleted-ids-ref touched-cells-ref]
  (let [field-name (name (:field column))]
    (clj->js
     {"field" field-name
      "headerName" (:title column)
      "headerComponent" header-component
      "headerComponentParams" column
      "editable" (fn [params]
                   (let [row (js->clj (j/get params :data) :keywordize-keys true)]
                     (not (contains? (.-current deleted-ids-ref) (row-id row)))))
      "flex" 1 "minWidth" 120
      "valueSetter" (partial value-setter column touched-cells-ref)
      "cellClassRules"
      {"webform-invalid"
       (fn [params]
         (invalid-cell? column
                        (js->clj (j/get params :data) :keywordize-keys true)
                        fields (.-current deleted-ids-ref)
                        (.-current touched-cells-ref)))}})))

(defn table
  [{:keys [api-ref rows columns fields baseline page items-per-page dark-mode?
           deleted-ids-ref touched-cells-ref on-state-change]}]
  (let [baseline-ref (react/useRef baseline)
        _ (set! (.-current baseline-ref) baseline)
        row-data (react/useMemo #(clj->js rows) #js [rows])
        row-selection
        (react/useMemo
         (fn []
           #js {:mode "multiRow"
                :checkboxes true
                :headerCheckbox true
                :hideDisabledCheckboxes true
                :enableClickSelection false
                :selectAll "all"
                :isRowSelectable
                (fn [node]
                  (let [row (js->clj (j/get node :data) :keywordize-keys true)]
                    (boolean (or (:-kwf-uuid row)
                                 (meaningful-row? row fields)))))})
         #js [fields])
        column-defs
        (react/useMemo
         #(into-array
           (concat [(row-number-column page items-per-page)]
                   (map (fn [column]
                          (data-column column fields deleted-ids-ref touched-cells-ref))
                        columns)
                   [(action-column api-ref fields deleted-ids-ref on-state-change)]))
         #js [columns fields page items-per-page])]
    [:div {:class "webforms-ag-grid w-full"}
     [:> AgGridReact
      {:theme (theme-for-mode dark-mode?)
       :suppress-field-dot-notation true
       :row-data row-data
       :column-defs column-defs
       :default-col-def #js {:sortable false :filter false :resizable false
                             :suppressMovable true}
       :dom-layout "autoHeight"
       :header-height 45
       :row-height 32
       :row-selection row-selection
       :selection-column-def #js {:width 36 :minWidth 36 :maxWidth 36
                                  :pinned "left" :lockPinned true
                                  :lockPosition true :suppressMovable true
                                  :sortable false :filter false :resizable false
                                  :editable false}
       :undo-redo-cell-editing true
       :undo-redo-cell-editing-limit 20
       :enter-navigates-vertically true
       :enter-navigates-vertically-after-edit true
       :get-row-id #(j/get-in % [:data (name row-id-key)])
       :get-row-class
       (fn [params]
         (str "webform-"
              (name (row-status
                     (js->clj (j/get params :data) :keywordize-keys true)
                     (.-current baseline-ref) fields
                     (.-current deleted-ids-ref)))))
       :on-grid-ready #(set! (.-current api-ref) (j/get % :api))
       :on-selection-changed #(on-state-change)
       :on-cell-value-changed
       (fn [params]
         (j/call (j/get params :api) :refreshClientSideRowModel "map")
         (on-state-change)
         (j/call (j/get params :api) :redrawRows
                 #js {:rowNodes #js [(j/get params :node)]}))}]]))
